// Phone input with country flag/code selector. Libya default; a few common
// countries pre-loaded. The exported .value is always E.164 (+<code><digits>).
import { useMemo, useState, useRef, useEffect } from "react";
import { ChevronDown, Phone } from "lucide-react";

// Libya first (default), then a small selection of Maghreb/Arab neighbors that
// are the most likely alt-country users in Libya's diaspora.
const COUNTRIES = [
  { code: "LY", flag: "🇱🇾", dial: "218", name: "ليبيا",   pattern: /^9\d{8}$/,     hint: "9XXXXXXXX" },
  { code: "TN", flag: "🇹🇳", dial: "216", name: "تونس",    pattern: /^\d{8}$/,      hint: "XXXXXXXX" },
  { code: "EG", flag: "🇪🇬", dial: "20",  name: "مصر",     pattern: /^1\d{9}$/,     hint: "1XXXXXXXXX" },
  { code: "DZ", flag: "🇩🇿", dial: "213", name: "الجزائر", pattern: /^[567]\d{8}$/, hint: "5XXXXXXXX" },
  { code: "MA", flag: "🇲🇦", dial: "212", name: "المغرب",  pattern: /^[67]\d{8}$/,  hint: "6XXXXXXXX" },
  { code: "SA", flag: "🇸🇦", dial: "966", name: "السعودية",pattern: /^5\d{8}$/,     hint: "5XXXXXXXX" },
  { code: "AE", flag: "🇦🇪", dial: "971", name: "الإمارات",pattern: /^5\d{8}$/,     hint: "5XXXXXXXX" },
  { code: "TR", flag: "🇹🇷", dial: "90",  name: "تركيا",   pattern: /^5\d{9}$/,     hint: "5XXXXXXXXX" },
  { code: "US", flag: "🇺🇸", dial: "1",   name: "أمريكا",   pattern: /^\d{10}$/,     hint: "XXXXXXXXXX" },
  { code: "GB", flag: "🇬🇧", dial: "44",  name: "بريطانيا", pattern: /^7\d{9}$/,     hint: "7XXXXXXXXX" },
];

const DEFAULT_COUNTRY = COUNTRIES[0];

/** Split an existing E.164 value into {country, local} on mount. */
function splitE164(value) {
  if (!value) return { country: DEFAULT_COUNTRY, local: "" };
  const digits = value.replace(/[^\d+]/g, "");
  const withPlus = digits.startsWith("+") ? digits : `+${digits}`;
  for (const c of COUNTRIES) {
    if (withPlus.startsWith(`+${c.dial}`)) {
      return { country: c, local: withPlus.slice(c.dial.length + 1) };
    }
  }
  // Fallback to Libya, keep raw digits after any leading 0.
  return { country: DEFAULT_COUNTRY, local: digits.replace(/^\+?218/, "").replace(/^0/, "") };
}

export default function PhoneInput({
  value = "",
  onChange,
  placeholder,
  disabled = false,
  dataTestid = "phone-input",
  autoFocus = false,
}) {
  const initial = useMemo(() => splitE164(value), [value]);
  const [country, setCountry] = useState(initial.country);
  const [local, setLocal] = useState(initial.local);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  const emit = (nextCountry, nextLocal) => {
    const digits = (nextLocal || "").replace(/\D/g, "").replace(/^0+/, "");
    onChange?.(digits ? `+${nextCountry.dial}${digits}` : "", { valid: nextCountry.pattern.test(digits), country: nextCountry, local: digits });
  };

  const pick = (c) => {
    setCountry(c);
    setOpen(false);
    emit(c, local);
    inputRef.current?.focus();
  };

  const onLocalChange = (e) => {
    // Strip non-digits + leading zeros to simplify.
    const digits = e.target.value.replace(/\D/g, "").replace(/^0+/, "");
    setLocal(digits);
    emit(country, digits);
  };

  return (
    <div className="relative" ref={wrapRef} data-testid={dataTestid}>
      <div className={`flex items-stretch rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus-within:bg-white focus-within:border-primary-brand overflow-hidden ${disabled ? "opacity-70" : ""}`}>
        <button
          type="button"
          onClick={() => !disabled && setOpen(!open)}
          disabled={disabled}
          className="flex items-center gap-1 px-3 py-3 bg-white border-l border-[var(--border)] hover:bg-blue-50 shrink-0"
          data-testid={`${dataTestid}-country-btn`}
        >
          <span className="text-lg leading-none">{country.flag}</span>
          <span className="text-xs font-mono font-bold text-[var(--text-secondary)]" dir="ltr">+{country.dial}</span>
          <ChevronDown size={12} className="text-[var(--text-secondary)]"/>
        </button>
        <div className="relative flex-1">
          <Phone size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] pointer-events-none"/>
          <input
            ref={inputRef}
            type="tel"
            inputMode="tel"
            dir="ltr"
            value={local}
            onChange={onLocalChange}
            disabled={disabled}
            placeholder={placeholder || country.hint}
            className="w-full pr-9 pl-3 py-3 bg-transparent focus:outline-none text-right"
            data-testid={`${dataTestid}-input`}
            autoComplete="tel-national"
          />
        </div>
      </div>

      {open && (
        <div
          className="absolute top-full right-0 mt-2 w-64 max-h-72 overflow-y-auto bg-white rounded-2xl talabya-shadow-float border border-[var(--border)] z-50 animate-fade-up"
          data-testid={`${dataTestid}-dropdown`}
        >
          {COUNTRIES.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => pick(c)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-right hover:bg-blue-50 ${country.code === c.code ? "bg-blue-50" : ""}`}
              data-testid={`${dataTestid}-country-${c.code}`}
            >
              <span className="text-lg">{c.flag}</span>
              <span className="flex-1 text-sm font-bold">{c.name}</span>
              <span className="font-mono text-xs text-[var(--text-secondary)]" dir="ltr">+{c.dial}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
